import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Box from 'core/ui/Box';
import DatePickerRounded from 'core/ui/DatePickers/DatePickerRounded';
import { ButtonPrimary } from 'core/ui/Button';
import { Search } from '@material-ui/icons';
import { formatDateToApi } from 'core/utils/formatters';
import { channelReportsModule } from 'modules/channelReports';
import useSelectors from 'modules/map/useSelectors';
import { timeZoneName } from 'core/utils/formatters/date';
import Typography from 'core/ui/Typography';
import useActions from 'modules/map/useActions';
import Modules from 'modules';
import LoaderSm from 'core/ui/LoaderSm';
import Grid from 'core/ui/Grid';
import ChoosenChannelTags from 'core/ui/ChoosenChannelTags';
import SelectBoxObject from 'core/ui/SelectBoxObject';
import Checkbox from 'core/ui/Checkbox';
import ResultUsers from './ResultUsers';
import SkeletonPayamentsReport from './Skeleton';
import BarMonths from './BarMonths';

const Attendance = ({ channel, loading, getReport, genericCheckIn }) => {
  const { t } = useTranslation();
  const { request } = useActions();
  const loadingAttendance = useSelectors(channelReportsModule, 'loadingAttendance');
  const [attendanceData, setAttendanceData] = useState();
  const data = useSelectors(channelReportsModule, 'data');
  const dateNow = new Date();
  const [filters, setFilters] = useState({
    startAt: `${dateNow.getMonth() + 1}/1/${dateNow.getFullYear()}`,
    endAt: `${dateNow.getMonth() + 1}/${dateNow.getDate()}/${dateNow.getFullYear()}`,
    usersWithNoAttendances: false,
  });

  const search = () => {
    const startAt = filters.startAt ? formatDateToApi(filters.startAt, timeZoneName()) : null;
    const endAt = filters.endAt ? formatDateToApi(filters.endAt, timeZoneName()) : null;
    if (startAt && endAt) getReport({ ...filters, startAt, endAt });
  };

  const attendanceResume = () => {
    request({
      action: Modules.channelReports.actions.attendanceResume,
      data: {
        channelId: channel.id,
      },
      options: {
        onSuccess: (resp) => {
          setAttendanceData(resp);
        },
      },
    });
  };

  useEffect(() => {
    if (channel?.id) {
      search();
      attendanceResume();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel]);

  return (
    <Box mt={2}>
      <Box>
        <Box display="flex" flexDirection="column" gridGap={8}>
          <Box
            display="flex"
            alignItems="flex-end"
            justifyContent="flex-start"
            flexWrap="wrap"
            gridGap={8}
          >
            <Box>
              <DatePickerRounded
                label={t('channel.reports.filter.start').toUpperCase()}
                onChange={(v) => setFilters((prev) => ({ ...prev, startAt: v }))}
                value={filters.startAt}
                disablePast={false}
                usFormat={channel?.useUsDateFormat}
                autoOk
              />
            </Box>
            <Box>
              <DatePickerRounded
                label={t('channel.reports.filter.end').toUpperCase()}
                onChange={(v) => setFilters((prev) => ({ ...prev, endAt: v }))}
                value={filters.endAt}
                disablePast={false}
                usFormat={channel?.useUsDateFormat}
                autoOk
              />
            </Box>
            <Box>
              <SelectBoxObject
                label="CLASS TYPE"
                bordered
                value={filters?.workoutsType}
                setValue={(v) => setFilters((prev) => ({ ...prev, workoutsType: v }))}
                options={channel?.workoutsTypes}
              />
            </Box>

            <Box>
              <ButtonPrimary disabled={loading} onClick={() => search()}>
                <Search style={{ color: 'white' }} fontSize="small" />
                {t('button.search')}&nbsp;
              </ButtonPrimary>
            </Box>
          </Box>

          <Box
            display="flex"
            alignItems="flex-end"
            justifyContent="flex-start"
            flexWrap="wrap"
            gridGap={8}
          >
            <Box>
              <Checkbox
                label={t('channel.reports.zeroAttendance')}
                checked={filters?.usersWithNoAttendances}
                onClick={() =>
                  setFilters((prev) => ({
                    ...prev,
                    usersWithNoAttendances: !prev.usersWithNoAttendances,
                  }))
                }
              />
            </Box>
          </Box>
        </Box>
        <Grid container spacing={5} mb={3} mt={3}>
          <Grid item xs={12} md={4}>
            <ChoosenChannelTags
              label={t('channel.label.memberTags')}
              placeholder={t('channel.tags')}
              val={filters?.membersTags ? [...filters?.membersTags] : []}
              setValue={(v) =>
                setFilters((prev) => ({ ...prev, membersTags: v?.map((x) => x?.name || x) }))
              }
              channelId={channel?.id}
              bordered
            />
          </Grid>
        </Grid>

        {!data?.users || loading ? (
          <SkeletonPayamentsReport />
        ) : (
          <Box>
            <ResultUsers
              channel={channel}
              data={data?.users || []}
              genericCheckIn={genericCheckIn}
            />

            <Box mt={3}>
              <BarMonths chartData={data?.chart} />
            </Box>

            <Box mt={3}>
              <Typography align="center">{t('label.totalAttendance')}</Typography>
              <LoaderSm center loading={loadingAttendance} />
              <BarMonths chartData={attendanceData} />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

Attendance.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  loading: PropTypes.bool,
  getReport: PropTypes.func,
};

Attendance.defaultProps = {
  loading: false,
  getReport: () => {},
};

export default Attendance;
